/**
 * Engagement layer for <ix-ig-post> (sections/ig-post-carousel.liquid).
 *
 * Purely additive on top of assets/ix-ig-post.js — the commerce core
 * (carousel sync, info bar, add-to-cart, variant sheet) is untouched.
 *
 * - double-tap the artwork to like, with a heart burst overlay
 * - like / save toggles with pop animation, persisted per section in
 *   localStorage; like count = base count from settings + your like
 * - share via the Web Share API, falling back to copy-link + toast
 * - caption clamps to two lines with a "more" / "less" toggle
 * - slide counter fades out after a moment of inactivity
 * - prev/next arrows on fine-pointer devices
 * - subtle parallax on slide artwork while swiping
 */

const REDUCED_MOTION = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const storage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* private mode: state just doesn't persist */
    }
  },
};

function formatCount(n) {
  return n.toLocaleString('en-US');
}

function init(post) {
  if (post.dataset.fxInit) return;
  post.dataset.fxInit = '1';

  const key = post.id || 'ig-post';
  const viewport = post.querySelector('.ig-post__viewport');
  const track = post.querySelector('.ig-post__track');
  const slides = Array.from(post.querySelectorAll('.ig-post__slide'));
  if (!viewport || !track || !slides.length) return;

  const currentSlide = () => {
    let nearest = slides[0];
    let nearestDistance = Infinity;
    slides.forEach((slide) => {
      const distance = Math.abs(slide.offsetLeft - track.scrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearest = slide;
      }
    });
    return nearest;
  };

  /* ---------- toast ---------- */
  const toastEl = post.querySelector('.ig-post__toast');
  let toastTimer = null;
  const toast = (message) => {
    if (!toastEl) return;
    toastEl.textContent = message;
    toastEl.hidden = false;
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => {
      toastEl.hidden = true;
    }, 1600);
  };

  /* ---------- like + count ---------- */
  const likeBtn = post.querySelector('.ig-post__action--like');
  const likesEl = post.querySelector('.ig-post__likes');
  const likesBase = Number(likesEl?.dataset.likesBase || 0);
  let liked = storage.get(`${key}:liked`) === '1';

  const renderLikes = () => {
    if (likeBtn) likeBtn.setAttribute('aria-pressed', liked ? 'true' : 'false');
    if (!likesEl) return;
    const total = likesBase + (liked ? 1 : 0);
    if (total > 0) {
      likesEl.hidden = false;
      likesEl.textContent = `${formatCount(total)} ${total === 1 ? 'like' : 'likes'}`;
    } else {
      likesEl.hidden = true;
    }
  };

  const setLiked = (value) => {
    liked = value;
    storage.set(`${key}:liked`, liked ? '1' : '0');
    renderLikes();
  };

  renderLikes();
  likeBtn?.addEventListener('click', () => setLiked(!liked));

  /* ---------- double-tap to like + heart burst ---------- */
  const burst = post.querySelector('.ig-post__burst');
  const fireBurst = () => {
    if (!burst || REDUCED_MOTION) return;
    burst.classList.remove('is-burst');
    // restart the animation even on rapid double-taps
    void burst.offsetWidth;
    burst.classList.add('is-burst');
  };
  burst?.addEventListener('animationend', () => burst.classList.remove('is-burst'));

  let lastTap = 0;
  let lastX = 0;
  let lastY = 0;
  viewport.addEventListener('pointerup', (event) => {
    if (event.target.closest('a, button, .ig-post__info-bar')) return;
    const now = Date.now();
    const isDouble = now - lastTap < 350 && Math.hypot(event.clientX - lastX, event.clientY - lastY) < 40;
    if (isDouble) {
      lastTap = 0;
      if (!liked) setLiked(true);
      fireBurst();
    } else {
      lastTap = now;
      lastX = event.clientX;
      lastY = event.clientY;
    }
  });

  /* ---------- save ---------- */
  const saveBtn = post.querySelector('.ig-post__action--save');
  if (saveBtn) {
    let saved = storage.get(`${key}:saved`) === '1';
    saveBtn.setAttribute('aria-pressed', saved ? 'true' : 'false');
    saveBtn.addEventListener('click', () => {
      saved = !saved;
      storage.set(`${key}:saved`, saved ? '1' : '0');
      saveBtn.setAttribute('aria-pressed', saved ? 'true' : 'false');
      if (saved) toast('Saved');
    });
  }

  /* ---------- share ---------- */
  post.querySelector('.ig-post__action--share')?.addEventListener('click', async () => {
    const slide = currentSlide();
    const url = new URL(slide?.dataset.url || window.location.pathname, window.location.origin).href;
    const title = slide?.dataset.title || document.title;
    if (navigator.share) {
      try {
        await navigator.share({ title, url });
      } catch {
        /* user dismissed the share sheet */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      toast('Link copied');
    } catch {
      toast(url);
    }
  });

  /* ---------- caption "more" toggle ---------- */
  const caption = post.querySelector('.ig-post__caption');
  const moreBtn = post.querySelector('.ig-post__more');
  if (caption && moreBtn) {
    const check = () => {
      if (!caption.classList.contains('is-clamped')) return;
      moreBtn.hidden = caption.scrollHeight <= caption.clientHeight + 2;
    };
    moreBtn.addEventListener('click', () => {
      const clamped = caption.classList.toggle('is-clamped');
      moreBtn.textContent = clamped ? 'more' : 'less';
    });
    check();
    if ('ResizeObserver' in window) new ResizeObserver(check).observe(caption);
  }

  /* ---------- counter fades when idle ---------- */
  const counter = post.querySelector('.ig-post__counter');
  let idleTimer = null;
  const armIdle = () => {
    if (!counter) return;
    counter.classList.remove('is-idle');
    clearTimeout(idleTimer);
    idleTimer = setTimeout(() => counter.classList.add('is-idle'), 1800);
  };
  armIdle();

  /* ---------- arrows (fine pointers) ---------- */
  const navButtons = Array.from(post.querySelectorAll('.ig-post__nav'));
  const syncNav = () => {
    if (!navButtons.length) return;
    const max = track.scrollWidth - track.clientWidth - 2;
    navButtons.forEach((button) => {
      const dir = Number(button.dataset.nav);
      button.disabled = dir < 0 ? track.scrollLeft <= 2 : track.scrollLeft >= max;
    });
  };
  navButtons.forEach((button) => {
    button.addEventListener('click', () => {
      track.scrollBy({
        left: Number(button.dataset.nav) * track.clientWidth,
        behavior: REDUCED_MOTION ? 'auto' : 'smooth',
      });
    });
  });
  syncNav();

  /* ---------- parallax + scroll-linked bits ---------- */
  const applyParallax = () => {
    if (REDUCED_MOTION) return;
    const width = track.clientWidth || 1;
    slides.forEach((slide) => {
      const rel = slide.offsetLeft - track.scrollLeft;
      if (Math.abs(rel) > width) return;
      const img = slide.querySelector('.ig-post__img');
      if (img) img.style.transform = `translateX(${(rel * -0.14).toFixed(1)}px) scale(1.04)`;
    });
  };
  applyParallax();

  let scrollPending = false;
  track.addEventListener(
    'scroll',
    () => {
      armIdle();
      if (scrollPending) return;
      scrollPending = true;
      requestAnimationFrame(() => {
        scrollPending = false;
        syncNav();
        applyParallax();
      });
    },
    { passive: true }
  );

  /* ---------- info bar: view-product link, tick, add feedback ---------- */
  const infoBar = post.querySelector('.ig-post__info-bar');
  const infoMain = post.querySelector('.ig-post__info-main');
  if (infoBar && infoMain) {
    const syncInfoLink = () => {
      const url = currentSlide()?.dataset.url;
      if (url) infoMain.setAttribute('href', url);
      else infoMain.removeAttribute('href');
    };
    syncInfoLink();

    // the core rewrites [data-title] on every scroll frame; only tick when
    // the slide actually changed
    const infoTitle = infoBar.querySelector('[data-title]');
    if (infoTitle) {
      let lastTitle = infoTitle.textContent;
      new MutationObserver(() => {
        if (infoTitle.textContent === lastTitle) return;
        lastTitle = infoTitle.textContent;
        syncInfoLink();
        if (!REDUCED_MOTION) {
          infoMain.classList.remove('is-tick');
          void infoMain.offsetWidth;
          infoMain.classList.add('is-tick');
        }
      }).observe(infoTitle, { childList: true });
      infoMain.addEventListener('animationend', () => infoMain.classList.remove('is-tick'));
    }

    // direct-add feedback: plus morphs to a check + toast. Multi-option
    // products open the sheet instead, which has its own feedback.
    const addBtn = infoBar.querySelector('.ig-post__add');
    addBtn?.addEventListener('click', () => {
      const slide = currentSlide();
      if (!slide?.dataset.variant || slide.dataset.available === 'false') return;
      if (Number(slide.dataset.opts || 0) > 1) return;
      addBtn.classList.add('is-added');
      setTimeout(() => addBtn.classList.remove('is-added'), 1500);
      setTimeout(() => toast('Added to bag'), 650);
    });
  }

  /* ---------- variant sheet: product context + live selection ---------- */
  const sheet = post.querySelector('.ig-post__sheet');
  const sheetGrid = post.querySelector('.ig-post__sheet-grid');
  const sheetProduct = post.querySelector('.ig-post__sheet-product');
  if (sheet && sheetGrid) {
    const thumb = post.querySelector('.ig-post__sheet-thumb');
    const vendorEl = post.querySelector('.ig-post__sheet-vendor');
    const titleEl = post.querySelector('.ig-post__sheet-ptitle');
    const selEl = post.querySelector('.ig-post__sheet-sel');
    const priceEl = post.querySelector('.ig-post__sheet-price');
    let productData = null;
    let atcClicked = false;

    const formatMoney = (cents) => {
      const currency = window.Shopify?.currency?.active || '';
      return `${(cents / 100).toFixed(2)} ${currency}`.trim();
    };

    const sizedImage = (src, width) => {
      try {
        const url = new URL(src, window.location.origin);
        url.searchParams.set('width', String(width));
        return url.href;
      } catch {
        return src;
      }
    };

    const fillHeaderFromSlide = () => {
      if (!sheetProduct) return;
      const slide = currentSlide();
      if (!slide?.dataset.title) {
        sheetProduct.hidden = true;
        return;
      }
      sheetProduct.hidden = false;
      if (vendorEl) vendorEl.textContent = slide.dataset.vendor || '';
      if (titleEl) titleEl.textContent = slide.dataset.title || '';
      if (selEl) selEl.textContent = '';
      if (priceEl) priceEl.textContent = slide.dataset.priceText || '';
      const img = slide.querySelector('.ig-post__img');
      if (thumb) {
        const src = img?.currentSrc || img?.src || '';
        thumb.hidden = !src;
        if (src) thumb.src = src;
      }
    };

    const readSelection = () =>
      Array.from(sheetGrid.querySelectorAll('.ig-post__pills')).map(
        (pills) => pills.querySelector('[aria-pressed="true"]')?.textContent?.trim() ?? null
      );

    const syncSelection = () => {
      const selection = readSelection();
      if (selEl) selEl.textContent = selection.filter(Boolean).join(' · ');
      if (!productData) return;
      const variant = productData.variants.find((v) => v.options.every((value, i) => value === selection[i]));
      if (!variant) return;
      if (priceEl) priceEl.textContent = formatMoney(variant.price);
      const variantImage = variant.featured_image?.src;
      if (variantImage && thumb && !thumb.src.includes(variantImage.split('?')[0])) {
        thumb.style.opacity = '0';
        const next = new Image();
        next.onload = () => {
          thumb.src = next.src;
          thumb.style.opacity = '';
        };
        next.src = sizedImage(variantImage, 112);
      }
    };

    new MutationObserver(() => {
      if (!sheet.hidden) {
        atcClicked = false;
        fillHeaderFromSlide();
        const handle = currentSlide()?.dataset.handle;
        productData = null;
        if (handle) {
          fetch(`/products/${handle}.js`)
            .then((response) => response.json())
            .then((data) => {
              productData = data;
              syncSelection();
            })
            .catch(() => {});
        }
      } else if (atcClicked) {
        atcClicked = false;
        toast('Added to bag');
      }
    }).observe(sheet, { attributes: true, attributeFilter: ['hidden'] });

    // the core re-renders the grid on every pill tap
    new MutationObserver(syncSelection).observe(sheetGrid, { childList: true });

    sheetGrid.addEventListener('click', (event) => {
      if (event.target.closest('.ig-post__atc:not([disabled])')) atcClicked = true;
    });

    /* swipe down to dismiss (mobile bottom-sheet mode only) */
    const closeBtn = post.querySelector('.ig-post__sheet-close');
    const dragFrom = [post.querySelector('.ig-post__sheet-handle'), post.querySelector('.ig-post__sheet-header')];
    let dragStart = null;
    let dragDelta = 0;

    const onDragMove = (event) => {
      if (dragStart == null) return;
      dragDelta = Math.max(0, event.clientY - dragStart);
      sheet.style.transform = `translateY(${dragDelta}px)`;
    };

    const onDragEnd = () => {
      if (dragStart == null) return;
      dragStart = null;
      sheet.classList.remove('is-dragging');
      sheet.style.transform = '';
      if (dragDelta > 110) closeBtn?.click();
      dragDelta = 0;
      window.removeEventListener('pointermove', onDragMove);
      window.removeEventListener('pointerup', onDragEnd);
      window.removeEventListener('pointercancel', onDragEnd);
    };

    dragFrom.forEach((zone) => {
      zone?.addEventListener('pointerdown', (event) => {
        if (event.target.closest('button')) return;
        if (!window.matchMedia('(max-width: 749px)').matches) return;
        dragStart = event.clientY;
        dragDelta = 0;
        sheet.classList.add('is-dragging');
        window.addEventListener('pointermove', onDragMove);
        window.addEventListener('pointerup', onDragEnd);
        window.addEventListener('pointercancel', onDragEnd);
      });
    });
  }
}

function initAll(root) {
  root.querySelectorAll('ix-ig-post').forEach(init);
}

initAll(document);

// Re-run for sections re-rendered by the theme editor.
document.addEventListener('shopify:section:load', (event) => initAll(event.target));
